import React, { useEffect } from 'react'
import { Flex, Box, Text } from 'rebass'
import { GridColDef } from '@mui/x-data-grid-pro'
import { theme } from 'theme'
import { useDispatch, useSelector } from 'react-redux'
import { useNewTranslation } from 'hooks/index'
import { selectRequestsAreLoading } from 'state'
import { STATE_KEYS } from 'state/requests'
import {
    selectBannerGroups,
    selectBanners,
    getBannerGroups,
    getBanners,
    clearBanners,
    selectBannerGroupCountry,
} from 'state/banners'
import PartiTable from 'components/Shared/MuiTable/PartiTable'
import { getColumns, getRows, RowsProps } from './BannersTableDef'
import { TableViewType } from 'components/Shared/MuiTable'
import { MAX_PAGE_SIZE, selectActivePageNum } from 'state/review'
import Divider from 'components/Divider'
import { RouteComponentProps } from 'react-router-dom'
import qs from 'query-string'
import { RectangleLinkButton as BaseRectangleLinkButton } from 'particles/Button/RectangleButton'
import styled from 'particles/themes/styled'

interface BannersProps {
    bannerGroupUuid: string
}

const RectangleLinkButton = styled(BaseRectangleLinkButton)`
    margin-bottom: 16px;
`

const Banners: React.FC<BannersProps> = ({ bannerGroupUuid }) => {
    const [newT] = useNewTranslation()
    const dispatch = useDispatch()
    const isLoading = useSelector(selectRequestsAreLoading([STATE_KEYS.GetBannerGroups, STATE_KEYS.GetBanners]))
    const bannerGroups = useSelector(selectBannerGroups)
    const banners = useSelector(selectBanners)
    const activePageNum = useSelector(selectActivePageNum)
    const country = useSelector(selectBannerGroupCountry(bannerGroupUuid))

    useEffect(() => {
        dispatch(clearBanners())
        if (bannerGroups.length == 0) {
            dispatch(getBannerGroups())
        }
        if (bannerGroupUuid) {
            const offset: number = activePageNum * MAX_PAGE_SIZE
            dispatch(getBanners({ bannerGroupUuid, offset }))
        }
    }, [activePageNum, bannerGroups.length, bannerGroupUuid])

    const bannersColumns: GridColDef[] = getColumns()
    const bannersRows: RowsProps[] = getRows(banners, bannerGroupUuid, activePageNum, country)
    return (
        <Flex>
            <Box flex={1} m={'24px'}>
                {country != null && (
                    <Box>
                        <Text
                            fontSize={theme.fontSizes[3]}
                            fontWeight={'bold'}
                            color={theme.colors.mineShaft}
                            textAlign={'left'}
                        >
                            {newT('country.' + country) + ' ' + newT('bannerGroup.banner.title', 'Banners')}
                        </Text>
                        <Divider mt={'24px'} mb={'24px'} sx={{ borderWidth: '4px' }} />
                    </Box>
                )}
                <RectangleLinkButton
                    data-testid="test-add-new-banner"
                    to={`/banners/editBanner?bannerGroup=${bannerGroupUuid}&page=${activePageNum}`}
                    form="filled"
                    size="m"
                    icon="plus"
                    label={newT('button.addNewBanner', 'Add New Banner')}
                />
                <PartiTable
                    rowHeight={120}
                    columns={bannersColumns}
                    rows={bannersRows}
                    isLoading={isLoading}
                    hideToolbar={true}
                    enableExport={false}
                    showFilter={false}
                    showSearchBar={false}
                    viewType={TableViewType.BANNER}
                    disableColumnMenu={true}
                />
            </Box>
        </Flex>
    )
}

const Wrapper: React.FC<RouteComponentProps> = ({ location }) => {
    const { bannerGroup: uuid } = qs.parse(location.search)
    if (typeof uuid !== 'string') return null
    return <Banners bannerGroupUuid={uuid} />
}

export default Wrapper
